import React, { useEffect, useRef, useState } from 'react';
import { css } from 'glamor';
import * as theme from '../../theme';
import { Alert } from '../Alert';
import { HollowButton } from '../Button';
import { H2 } from '../Typography';
import { ChangeBayPopover } from './ChangeBayPopover';
import { TYPE_SCALE } from './constants/typography';

/* "Allocated bay" section of the staff New Booking Parklio card (DRI-2874). Ported
 * from Nick's preview design but wired to our `parklioAvailableBays` data
 * (`{ id, label, zone }`): shows the picked bay + a Change-bay popover, defaults to
 * the first free bay (id ASC, mirroring the server), and warns when none is free.
 * Presentational — the parent owns `selectedBayId` + the availability query. */
const row = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'space-between',
	gap: 16,
	padding: '12px 16px',
	background: theme.gray05,
	borderRadius: 6,
	marginBottom: 16,
});
const bayLabelCss = css({ ...TYPE_SCALE.sectionHeading, color: theme.gray90 });
const bayZoneCss = css({ ...TYPE_SCALE.control, color: theme.gray50 });
const hint = css({ ...TYPE_SCALE.control, color: theme.gray50, marginTop: 2 });

export const AllocatedBaySection = ({
	bays,
	selectedBayId,
	onSelectBay,
	loading = false,
	error = null,
	onRetry,
}) => {
	const [popoverOpen, setPopoverOpen] = useState(false);
	const anchorRef = useRef(null);

	// Default to the first free bay (id ASC, mirroring the server's auto-assign);
	// re-pick when the current selection drops out of the list (dates changed or
	// the bay was taken). Idempotent — once a valid bay is selected it no-ops.
	useEffect(() => {
		if (loading) return;
		if (selectedBayId && bays.some((bay) => bay.id === selectedBayId)) return;
		onSelectBay(bays[0]?.id ?? null);
	}, [bays, selectedBayId, loading, onSelectBay]);

	const selected = bays.find((bay) => bay.id === selectedBayId) ?? null;

	const choose = (bay) => {
		onSelectBay(bay.id);
		setPopoverOpen(false);
	};

	return (
		<>
			<H2 spaceBelow="small">Allocated bay</H2>
			{error ? (
				<Alert flavour="danger" spaceBelow="medium">
					<strong>Couldn't load bay availability.</strong>
					<p>Availability could not be checked — this does not mean no bay is free.</p>
					{onRetry ? (
						<HollowButton size="small" spaceBelow="none" onClick={onRetry}>
							Retry
						</HollowButton>
					) : null}
				</Alert>
			) : loading ? (
				<div className={hint}>Checking availability…</div>
			) : selected ? (
				<div className={row}>
					<div>
						<div className={bayLabelCss}>
							{selected.label}
							{selected.zone ? (
								<span className={bayZoneCss}> · {selected.zone}</span>
							) : null}
						</div>
						<div className={hint}>Available for the full booking</div>
					</div>
					<span ref={anchorRef}>
						<HollowButton
							size="small"
							spaceBelow="none"
							onClick={() => setPopoverOpen(true)}
						>
							Change bay
						</HollowButton>
					</span>
				</div>
			) : (
				<Alert flavour="warning" spaceBelow="medium">
					<strong>No bay is available for the selected period.</strong>
					<p>Change the dates to free up a bay before creating this booking.</p>
				</Alert>
			)}
			<ChangeBayPopover
				anchorEl={anchorRef.current}
				open={popoverOpen}
				onClose={() => setPopoverOpen(false)}
				bays={bays}
				selectedBayId={selectedBayId}
				onSelect={choose}
			/>
		</>
	);
};
